import {EdgeProps, useReactFlow} from "@xyflow/react";
import {WorkflowNodeTypes} from "@/types/node";
import {generateId} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import {createNode} from "@/pages/workflow/NodeTypes/WorkflowNode";
import {createFloatingEdge} from "@/pages/workflow/EdgeTypes/FloatingEdge";
import useNode from "./useNode";

function useEdge(id: EdgeProps["id"]) {
  const {setEdges, setNodes, getNode, getEdge} = useReactFlow();
  const {getNodeParent} = useNode();

  const createPlaceholderNode = (position: {x: number; y: number}) => ({
    id: generateId(),
    position: {x: position.x, y: position.y},
    type: "placeholder",
    data: {type: "placeholder"},
  });

  const addEdges = (edgesToAdd: any[]) => {
    setEdges(edges => {
      const allEdges = edges.concat(edgesToAdd);
      return allEdges.map(e => ({...e, selected: false}));
    });
  };

  const addNodes = (nodesToAdd: any[]) => {
    setNodes(nodes => [...nodes, ...nodesToAdd]);
  };

  const addNodeOnEdge = (blueprint: any) => {
    const edge = getEdge(id);
    if (!edge) return;

    const targetNode = getNode(edge.target);
    const sourceNode = getNode(edge.source);
    if (!targetNode) return;

    const newNodeId = generateId();
    blueprint.id = newNodeId;
    const childNode = createNode(blueprint);

    const isTargetBranch = targetNode.type === WorkflowNodeTypes.BRANCH;
    const targetParentNode = isTargetBranch
      ? getNodeParent(targetNode.id)
      : null;

    const isSourceBranch = sourceNode?.type === WorkflowNodeTypes.BRANCH;
    const sourceParentNode = isSourceBranch
      ? getNodeParent(sourceNode.id)
      : null;

    const isBlueprintBranch = blueprint.type === WorkflowNodeTypes.BRANCH;
    const isBlueprintJump = blueprint.type === WorkflowNodeTypes.JUMP;
    const isBlueprintEnd = blueprint.type === WorkflowNodeTypes.END;

    const jumpToNodeId = isBlueprintJump
      ? (getSettingValueByKey(
          childNode.data.settings,
          "jump_to_step",
        ) as string)
      : null;

    if ((isTargetBranch || isSourceBranch) && isBlueprintBranch) {
      const parentNode = sourceParentNode ?? targetParentNode;

      blueprint.position = {
        x: parentNode.position.x,
        y: parentNode.position.y,
      };

      const siblingNode = createNode(blueprint);
      const siblingPlaceholder = createPlaceholderNode(siblingNode.position);

      const siblingEdges = [
        {
          id: `${parentNode.id}->${newNodeId}`,
          source: parentNode.id,
          target: newNodeId,
          type: "workflow",
        },
        {
          id: `${newNodeId}->${siblingPlaceholder.id}`,
          source: newNodeId,
          target: siblingPlaceholder.id,
          type: "workflow",
        },
      ];

      addEdges(siblingEdges);
      addNodes([siblingNode, siblingPlaceholder]);
      return;
    }

    // Add child node
    blueprint.position = {
      x: targetNode.position.x,
      y: targetNode.position.y,
    };

    const newEdges = [
      {
        id: `${edge.source}->${newNodeId}`,
        source: edge.source,
        target: newNodeId,
        type: "workflow",
      },
      {
        id: `${newNodeId}->${edge.target}`,
        source: newNodeId,
        target: edge.target,
        type: "workflow",
        ...(isBlueprintJump || isBlueprintEnd ? {hidden: true} : {}),
      },
      ...(isBlueprintJump
        ? [
            createFloatingEdge({
              sourceNodeId: newNodeId,
              targetNodeId: jumpToNodeId,
            }),
          ]
        : []),
    ];

    setEdges(edges => {
      const filteredEdges = edges.filter(e => e.id !== id);
      const updatedEdges = isTargetBranch
        ? filteredEdges.map(e =>
            e.source === edge.source ? {...e, source: newNodeId} : e,
          )
        : filteredEdges;
      return updatedEdges.concat(newEdges);
    });

    setNodes(nodes => {
      const targetIndex = nodes.findIndex(node => node.id === edge.target);
      return [
        ...nodes.slice(0, targetIndex),
        childNode,
        ...nodes.slice(targetIndex),
      ];
    });
  };

  return addNodeOnEdge;
}

export default useEdge;
